import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, getFirestore } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { AppStatus } from '../../constants/appStatus';

const useDashboardLogic = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('DashboardLogic: Setting up auth listener');
    // Use onAuthStateChanged to handle user login/logout changes
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      let unsubscribeFirestore = () => {}; // Firestore listener cleanup
      setLoading(true);
      setProjects([]); // Clear projects on user change

      if (user) {
        console.log('DashboardLogic: User signed in, setting up real-time listener', user.uid);
        // User is signed in - set up a real-time listener for their apps
        const appsRef = collection(db, 'userApps');
        const q = query(appsRef, where('userId', '==', user.uid));

        // Configure the snapshot to get data from cache first, then from server
        const options = {
          includeMetadataChanges: true,  // Include metadata changes
        };

        unsubscribeFirestore = onSnapshot(q, options, (querySnapshot) => {
          // Log whether the data comes from cache or server
          const source = querySnapshot.metadata.fromCache ? 'cache' : 'server';
          console.log(`DashboardLogic: Data came from ${source}, size: ${querySnapshot.size}`);
          
          const userApps = [];
          querySnapshot.forEach((doc) => {
            const appData = { id: doc.id, ...doc.data() };
            // Debug log to see what data we're getting
            console.log(`DashboardLogic: App data:`, {
              id: appData.id,
              fileName: appData.fileName,
              statusApp: appData.statusApp,
              uploadedAt: appData.uploadedAt
            });
            
            // Ensure statusApp exists or set a default to AWAITING_UPLOAD
            if (!appData.statusApp) {
              appData.statusApp = AppStatus.AWAITING_UPLOAD;
            }
            userApps.push(appData);
          });
          
          // Better sorting logic - first by uploadedAt, then by id if dates are identical
          userApps.sort((a, b) => {
            const dateA = a.uploadedAt?.toDate?.() || new Date(0);
            const dateB = b.uploadedAt?.toDate?.() || new Date(0);
            
            // If dates are similar (within 1 second), sort by ID instead
            // This helps when serverTimestamp hasn't been fully resolved yet
            if (Math.abs(dateA - dateB) < 1000) {
              return b.id.localeCompare(a.id); // Sort by ID (most recent typically has "higher" ID)
            }
            
            return dateB - dateA; // Sort by date (newest first)
          });
          
          console.log(`DashboardLogic: Setting ${userApps.length} projects`);
          setProjects(userApps);
          setLoading(false);
        }, (error) => {
          console.error("DashboardLogic: Error fetching user apps:", error);
          setLoading(false);
          setProjects([]); // Clear projects on error
        });

      } else {
        // User is logged out - clear projects and stop loading
        console.log('DashboardLogic: User logged out');
        setProjects([]);
        setLoading(false);
      }

      // Cleanup function for the Firestore listener
      return () => {
        console.log('DashboardLogic: Cleaning up Firestore listener');
        unsubscribeFirestore(); // Detach Firestore listener when user changes or component unmounts
      };
    });

    // Cleanup function for the auth listener
    return () => {
      console.log('DashboardLogic: Cleaning up auth listener');
      unsubscribeAuth(); // Detach auth listener when component unmounts
    };
  }, []); // Empty dependency array ensures this runs once on mount

  return {
    projects,
    loading,
  };
};

export default useDashboardLogic; 